import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/Logo';

export default function StoryPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#F5EFE7]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Logo className="w-12 h-12" color={isScrolled ? '#6B1F3D' : '#F5EFE7'} />
            <span className={`text-2xl font-['Great_Vibes'] ${isScrolled ? 'text-[#6B1F3D]' : 'text-[#F5EFE7]'}`}>
              Sacred Love
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="/#about" className={`${isScrolled ? 'text-[#6B1F3D]' : 'text-[#F5EFE7]'} hover:opacity-70 transition-opacity cursor-pointer font-['Lato']`}>About</a>
            <a href="/#events" className={`${isScrolled ? 'text-[#6B1F3D]' : 'text-[#F5EFE7]'} hover:opacity-70 transition-opacity cursor-pointer font-['Lato']`}>Services</a>
            <a href="/#gallery" className={`${isScrolled ? 'text-[#6B1F3D]' : 'text-[#F5EFE7]'} hover:opacity-70 transition-opacity cursor-pointer font-['Lato']`}>Gallery</a>
            <a href="/#contact" className={`px-6 py-2 rounded-full border-2 ${isScrolled ? 'border-[#6B1F3D] text-[#6B1F3D] hover:bg-[#6B1F3D] hover:text-white' : 'border-[#F5EFE7] text-[#F5EFE7] hover:bg-[#F5EFE7] hover:text-[#6B1F3D]'} transition-all cursor-pointer whitespace-nowrap font-['Lato']`}>
              Book Session
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-end overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://static.readdy.ai/image/49a56f973024f64ed0e5539001dfaf2d/ff7787791cd2b8ffa7157531212cc04e.png" 
            alt="Samphire Hayes"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent"></div>
        
        <div className="relative z-10 px-6 md:px-12 lg:px-20 max-w-2xl mr-0 lg:mr-20 flex items-center h-full">
          <p className="text-2xl md:text-3xl lg:text-4xl text-[#F5EFE7] font-['Lato'] leading-relaxed text-center lg:text-right">
            A journey of healing, discovery, and sacred connection
          </p>
        </div>
      </section>

      {/* Story Content */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-serif text-[#6B1F3D] mb-6">The Beginning</h2>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato'] mb-4">
                My journey into sacred sexuality and conscious relating began not in a temple or workshop, but in the quiet recognition that something essential was missing from the way we're taught to love, desire, and connect.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                Growing up, I learned that intimacy was something to be figured out in private, that desire was either shameful or trivial, and that the body was something to be managed rather than listened to. It took years of my own unraveling to realize how much of my aliveness had been buried beneath those inherited stories.
              </p>
            </div>

            <div>
              <h2 className="text-4xl font-serif text-[#6B1F3D] mb-6">The Training</h2>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato'] mb-4">
                I trained as a psychotherapist specializing in trauma, sexuality, and the body, drawn to the places where psychology meets embodiment. My work with ISTA (International School of Temple Arts) deepened my understanding of sacred sexuality as a practice of presence, consent, and transformation.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                But it was my own experiences in temple spaces, ceremonies, and relational practice that taught me what no training manual could: that healing happens not through fixing, but through feeling. That intimacy is not a skill to master, but a capacity to cultivate. That the erotic is not separate from the sacred, but one of its most honest expressions.
              </p>
            </div>

            <div>
              <h2 className="text-4xl font-serif text-[#6B1F3D] mb-6">The Work</h2>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato'] mb-4">
                Sacred Love emerged from a desire to create spaces where people could explore intimacy, desire, and connection without shame or performance. Where boundaries are honored as sacred, where consent is practiced as an art, and where shadow is welcomed as part of wholeness.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                Through group experiences, one-to-one integration sessions, and couples coaching, I support people in reclaiming their relationship to their bodies, their desires, and their capacity for authentic relating. This work is not about becoming someone new, but about remembering who you've always been beneath the conditioning.
              </p>
            </div>

            <div>
              <h2 className="text-4xl font-serif text-[#6B1F3D] mb-6">The Philosophy</h2>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato'] mb-4">
                I believe that our sexuality is inseparable from our spirituality, our creativity, and our capacity for joy. That the body holds wisdom the mind cannot access alone. That intimacy begins with self-trust, and that desire is a form of intelligence.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato'] mb-4">
                I believe in the power of ritual to mark transitions, in the necessity of shadow work, and in the radical act of showing up as we are. I believe that healing is not linear, that pleasure is our birthright, and that love, in all its forms, is worth the risk.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-['Lato']">
                Most of all, I believe that we are not broken. That beneath the shame, the silence, and the stories we've inherited, there is a wild, sacred aliveness waiting to be remembered.
              </p>
            </div>

            <div className="bg-[#F5EFE7] rounded-2xl p-8 mt-12">
              <h3 className="text-2xl font-serif text-[#6B1F3D] mb-4">Qualifications & Training</h3>
              <ul className="space-y-3 text-gray-700 font-['Lato']">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B1F3D] text-xl mt-1"></i>
                  <span>Qualified Psychotherapist specializing in trauma, sexuality, and somatic practice</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B1F3D] text-xl mt-1"></i>
                  <span>ISTA (International School of Temple Arts) Graduate</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B1F3D] text-xl mt-1"></i>
                  <span>Trained in conscious relating and sacred sexuality practices</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B1F3D] text-xl mt-1"></i>
                  <span>Experienced facilitator of group processes and ceremonial spaces</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#6B1F3D] text-xl mt-1"></i>
                  <span>Specialist in couples coaching and relational work</span>
                </li>
              </ul>
            </div>

            <div className="text-center pt-8">
              <a href="/#events" className="inline-flex items-center gap-2 bg-[#6B1F3D] text-[#F5EFE7] px-8 py-4 rounded-full hover:bg-[#8B2F4D] transition-all whitespace-nowrap cursor-pointer text-lg">
                <span>Explore Services</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#6B1F3D] text-[#F5EFE7] pt-20 pb-8 px-6 rounded-t-[40px]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {/* Newsletter */}
            <div>
              <h4 className="text-5xl font-serif mb-4">Stay Connected</h4>
              <p className="text-[#F5EFE7]/80 mb-4 font-['Lato']">
                Receive sacred wisdom, event updates, and spiritual insights delivered to your inbox
              </p>
            </div>
            
            {/* Explore */}
            <div>
              <h5 className="text-sm uppercase tracking-wider mb-6 text-[#F5EFE7]/90">Explore</h5>
              <div className="space-y-4">
                <a href="/#about" className="block text-[#F5EFE7] hover:text-white transition-colors border-b border-transparent hover:border-[#D4AF37] pb-1 w-fit cursor-pointer">About</a>
                <a href="/#events" className="block text-[#F5EFE7] hover:text-white transition-colors border-b border-transparent hover:border-[#D4AF37] pb-1 w-fit cursor-pointer">Services</a>
                <a href="/#gallery" className="block text-[#F5EFE7] hover:text-white transition-colors border-b border-transparent hover:border-[#D4AF37] pb-1 w-fit cursor-pointer">Gallery</a>
                <a href="/story" className="block text-[#F5EFE7] hover:text-white transition-colors border-b border-transparent hover:border-[#D4AF37] pb-1 w-fit cursor-pointer">Samphire's Story</a>
              </div>
            </div>
            
            {/* Connect */}
            <div>
              <h5 className="text-sm uppercase tracking-wider mb-6 text-[#F5EFE7]/90">Connect</h5>
              <div className="space-y-3 mb-8">
                <a href="mailto:hello@sacredlove.uk" className="block text-lg border-b border-[#F5EFE7] pb-1 w-fit hover:border-white transition-colors cursor-pointer font-['Lato']">hello@sacredlove.uk</a>
                <a 
                  href="https://instagram.com/sacredlove.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#F5EFE7] hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                  <span className="font-['Lato']">@sacredlove.uk</span>
                </a>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-[#F5EFE7]/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#F5EFE7]/70 text-sm font-['Lato']">© 2025 Sacred Love. All rights reserved.</p>
            
            <div className="flex gap-6">
              <a href="#privacy" className="text-[#F5EFE7] hover:text-white border-b border-transparent hover:border-[#D4AF37] pb-1 transition-colors cursor-pointer text-sm">Privacy Policy</a>
              <a href="#terms" className="text-[#F5EFE7] hover:text-white border-b border-transparent hover:border-[#D4AF37] pb-1 transition-colors cursor-pointer text-sm">Terms</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[#F5EFE7] hover:text-white border-b border-transparent hover:border-[#D4AF37] pb-1 transition-colors cursor-pointer text-sm">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
